import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { publicApi } from '@/lib/api';
import { Star, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({});
  const { t } = useLanguage();

  useEffect(() => {
    publicApi.getReviews().then(r => setReviews(r.data || [])).catch(() => {});
    publicApi.getSettings().then(r => setSettings(r.data || {})).catch(() => {});
  }, []);

  // Note Google admin (prioritaire), sinon moyenne des avis du site
  const googleRating = settings.google_rating ? Number(settings.google_rating) : null;
  const googleCount = settings.google_reviews_count ? Number(settings.google_reviews_count) : null;
  const googleUrl = settings.google_reviews_url || settings.maps_url || null;
  const googleUpdated = settings.google_reviews_updated_at || null;

  const localAvg = reviews.length > 0 ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length) : 0;
  const displayRating = googleRating !== null ? googleRating : localAvg;
  const displayRatingStr = displayRating.toFixed(1).replace('.', ',');

  return (
    <div className="min-h-screen bg-cream" data-testid="reviews-page">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">{t('reviews.sectionLabel')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-garden-800 mb-4">
            {t('reviews.title')}
          </h1>
          <p className="text-base md:text-lg text-charcoal/70 max-w-2xl">
            {settings.reviews_intro_text || t('reviews.description')}
          </p>
        </div>

        {/* Note Google + lien fiche */}
        {(googleRating !== null || reviews.length > 0) && ( // FIX-MOYENNE-02
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-garden-800/5 shadow-sm flex flex-col sm:flex-row items-center gap-6" data-testid="reviews-google-card">
              <div className="text-center">
                <span className="text-5xl font-heading font-bold text-garden-800" data-testid="reviews-rating-value">{displayRatingStr}</span>
                <span className="text-lg text-charcoal/50">/5</span>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="flex gap-1 mb-1 justify-center sm:justify-start">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={20} className={s <= Math.round(displayRating) ? 'text-sunny fill-sunny' : 'text-gray-300'} />
                  ))}
                </div>
                <p className="text-sm text-charcoal/60">
                  {googleRating !== null ? (
                    <>Note Google{googleCount ? ` · ${googleCount} avis` : ''}{googleUpdated ? ` · mise à jour le ${new Date(googleUpdated).toLocaleDateString('fr-FR')}` : ''}</>
                  ) : (
                    t('reviews.outOf')
                  )}
                </p>
                {googleUrl && (
                  <a
                    href={googleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-garden-700 hover:text-garden-900 underline underline-offset-2"
                    data-testid="reviews-google-link"
                  >
                    Voir les avis sur Google <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reviews Grid (avis collectés sur le site) */}
        {reviews.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, i) => (
                <div
                  key={review.id}
                  className={`bg-white rounded-2xl p-6 border border-garden-800/5 shadow-sm hover:shadow-md transition-shadow opacity-0 animate-fade-up`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                  data-testid={`review-item-${review.id}`}
                >
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={16} className={s <= review.rating ? 'text-sunny fill-sunny' : 'text-gray-300'} />
                    ))}
                  </div>
                  <p className="text-charcoal/80 text-sm leading-relaxed mb-4">"{review.comment || review.text}"</p>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-garden-800 text-sm">{review.customerName || review.name}</span>
                    <span className="text-xs text-charcoal/40">
                      {review.createdAt ? new Date(review.createdAt).toLocaleDateString('fr-FR') : (review.created_at ? new Date(review.created_at).toLocaleDateString('fr-FR') : '')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
